import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../data/session';
import { loginWithEmail, loginWithGoogle, friendlyAuthError, routeAfterAuth, refreshSession } from '../../services/auth-service';
import { auth } from '../../config/firebase';
import { Spinner } from '../../components';


const EyeIcon = ({ open }: { open: boolean }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const GoogleIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [submittingGoogle, setSubmittingGoogle] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = submittingEmail || submittingGoogle;

  useEffect(() => {
    auth.authStateReady().then(async () => {
      const u = getCurrentUser();
      if (!u.id) return;
      // For roles that require admin approval, always fetch fresh status so an
      // approved user isn't bounced to /pending based on a stale cached session.
      if (['partner', 'associate', 'investor'].includes(u.role) && u.status === 'pending') {
        try {
          const res = await refreshSession();
          if (res) { navigate(routeAfterAuth(res.user as any), { replace: true }); return; }
        } catch { /* fall through to cached route */ }
      }
      navigate(routeAfterAuth(u as any), { replace: true });
    });
  }, [navigate]);

  const handleAfterAuth = (res: { user: any; requiresEmailVerification: boolean }) => {
    const target = routeAfterAuth(res.user, { requiresEmailVerification: res.requiresEmailVerification });
    if (target === '/verify') {
      navigate('/verify', { state: { email, role: res.user.role } });
    } else {
      navigate(target);
    }
  };

  const handleSignIn = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setSubmittingEmail(true); setError(null);
    try {
      const res = await loginWithEmail(email, password);
      handleAfterAuth(res);
    } catch (err: any) {
      if (err?.code === 'NO_ACCOUNT') {
        setError("You don't have an account yet — please sign up.");
      } else {
        const msg = friendlyAuthError(err);
        if (msg) setError(msg);
      }
    } finally {
      setSubmittingEmail(false);
    }
  };

  const handleGoogle = async () => {
    setSubmittingGoogle(true); setError(null);
    try {
      const res = await loginWithGoogle();
      handleAfterAuth(res);
    } catch (err: any) {
      if (err?.code === 'NO_ACCOUNT') {
        setError("No account for this Google email yet — redirecting to sign up…");
        setTimeout(() => navigate('/register'), 1200);
      } else {
        const msg = friendlyAuthError(err);
        if (msg) setError(msg);
      }
    } finally {
      setSubmittingGoogle(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left graphic panel */}
      <div className="hidden lg:flex lg:w-[42%] bg-accent-soft relative overflow-hidden flex-col items-center justify-center p-12">
        <img
          src="/homepage-graphics/bubble-1.png"
          alt=""
          aria-hidden="true"
          className="absolute -bottom-16 -left-20 w-[500px] h-[500px] object-contain opacity-60 pointer-events-none select-none"
        />
        <div className="relative z-10 text-center space-y-6 max-w-xs">
          <img src="/logo.png" alt="BunyadKar" className="h-16 w-auto mx-auto" />
          <p className="font-serif-display text-2xl italic text-muted leading-relaxed">
            "Every big business begins with a small idea — and the right support to grow."
          </p>
          <div className="flex justify-center gap-1">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`h-1 rounded-full ${i === 0 ? 'w-6 bg-chart' : 'w-2 bg-chart/30'}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 bg-bg flex flex-col items-center justify-center p-6 lg:p-12 overflow-y-auto">
        {/* Back button */}
        <div className="w-full max-w-sm mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-text transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to home
          </Link>
        </div>
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src="/logo.png" alt="BunyadKar" className="h-10 w-auto mx-auto" />
          </div>

          <h1 className="font-display text-4xl tracking-wide text-text mb-1">Welcome back</h1>
          <p className="text-muted text-sm mb-8">Sign in to your account to continue</p>

          {/* Form */}
          <form onSubmit={handleSignIn} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-medium text-text mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 rounded-card border border-line bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-text mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2.5 pr-10 rounded-card border border-line bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart transition-all text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-text transition-colors"
                  tabIndex={-1}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
              <div className="text-right mt-1.5">
                <Link to="/forgot" className="text-xs text-chart hover:text-chart/80 transition-colors">
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <p className="text-danger text-xs -mt-1">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-chart text-white py-2.5 rounded-card font-medium hover:bg-chart/90 transition-all duration-200 shadow-sm text-sm mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submittingEmail && <Spinner className="w-4 h-4" />}
              {submittingEmail ? 'Signing in…' : 'Sign in'}
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-line" />
              <span className="text-xs text-muted">or</span>
              <div className="flex-1 h-px bg-line" />
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 bg-shell border border-line py-2.5 rounded-card font-medium text-text hover:bg-surface transition-all duration-200 text-sm shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submittingGoogle ? <Spinner className="w-4 h-4 text-text" /> : <GoogleIcon />}
              {submittingGoogle ? 'Signing in…' : 'Continue with Google'}
            </button>
          </form>

          <p className="text-center text-sm text-muted mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-chart hover:text-chart/80 font-medium transition-colors">
              Sign up
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
};

export default Login;
